import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { imgUrl, formatPrice } from "@/lib/api";
import { useCart } from "@/context/CartContext";

export const QuickView = ({ product, onClose }) => {
  const { addItem } = useCart();
  const [size, setSize] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    setSize(null);
    setActiveImg(0);
  }, [product]);

  if (!product) return null;
  const price = product.discountPrice || product.price;
  const images = product.images?.length ? product.images : [product.image];

  return (
    <AnimatePresence>
      {product && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] flex items-center justify-center p-4 md:p-8"
          onClick={onClose}
          data-testid="quick-view-modal"
        >
          <div className="absolute inset-0 bg-black/80 backdrop-blur-md" />
          <motion.div
            initial={{ scale: 0.92, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.92, opacity: 0, y: 20 }}
            transition={{ type: "spring", damping: 26, stiffness: 280 }}
            onClick={(e) => e.stopPropagation()}
            className="relative glass w-full max-w-4xl max-h-[88vh] overflow-y-auto grid md:grid-cols-2"
          >
            <button
              onClick={onClose}
              className="absolute top-4 right-4 z-10 text-neutral-300 hover:text-white bg-black/40 p-2 rounded-full"
              data-testid="quick-view-close"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>

            <div>
              <div className="aspect-[3/4] bg-[#1A1D24]">
                <img src={imgUrl(images[activeImg])} alt={product.name} className="w-full h-full object-cover" />
              </div>
              {images.length > 1 && (
                <div className="flex gap-2 p-3">
                  {images.map((im, i) => (
                    <button
                      key={i}
                      onClick={() => setActiveImg(i)}
                      className={`w-14 h-16 overflow-hidden border ${activeImg === i ? "border-white" : "border-white/10"}`}
                    >
                      <img src={imgUrl(im)} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="p-6 md:p-8 flex flex-col">
              <p className="font-mono-th text-[10px] uppercase tracking-[0.25em] text-neutral-500">{product.category}</p>
              <h2 className="font-display font-bold text-2xl mt-2 leading-tight">{product.name}</h2>
              <div className="flex items-center gap-3 mt-3">
                <span className="font-display font-semibold text-xl">{formatPrice(price)}</span>
                {product.discountPrice && (
                  <span className="text-neutral-600 line-through">{formatPrice(product.price)}</span>
                )}
              </div>
              <p className="text-neutral-400 text-sm mt-4 leading-relaxed line-clamp-4">{product.description}</p>

              <p className="font-mono-th text-[10px] uppercase tracking-[0.25em] text-neutral-500 mt-6 mb-3">Select Size</p>
              <div className="flex flex-wrap gap-2">
                {product.sizes?.map((s) => (
                  <button
                    key={s}
                    onClick={() => setSize(s)}
                    className={`min-w-11 h-11 px-3 border font-mono-th text-sm transition-all ${
                      size === s ? "bg-white text-black border-white" : "border-white/20 hover:border-white/50"
                    }`}
                    data-testid={`quick-view-size-${s}`}
                  >
                    {s}
                  </button>
                ))}
              </div>

              <div className="mt-auto pt-8 space-y-3">
                <button
                  onClick={() => {
                    if (!size) return;
                    addItem(product, size);
                    onClose();
                  }}
                  disabled={!size}
                  className="w-full bg-white text-black py-4 font-mono-th text-xs uppercase tracking-[0.2em] font-semibold disabled:opacity-40 hover:bg-neutral-200 transition-colors"
                  data-testid="quick-view-add-btn"
                >
                  {size ? "Add to Bag" : "Select a size"}
                </button>
                <button
                  onClick={() => navigate(`/product/${product.id}`)}
                  className="w-full border border-white/20 py-4 font-mono-th text-xs uppercase tracking-[0.2em] hover:bg-white/5 transition-colors"
                  data-testid="quick-view-details-btn"
                >
                  View Full Details
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
